/* eslint-disable no-param-reassign */
import { Tronic } from '..';
import { IframeController } from '../iframe-controller';
import { LauncherConfig } from './types';
import { SimpleWidget } from './simple-widget';
import { applyIframePositioning, IframePositioningConfig } from '../utils/css-in-js';

export class LauncherController {
  private sdk: Tronic;
  private widget: SimpleWidget;
  private config: LauncherConfig;

  constructor(sdk: Tronic, config: LauncherConfig = {}) {
    this.sdk = sdk;
    this.config = {
      position: 'bottom-right',
      ...config,
    };

    // Create the simplified widget
    this.widget = new SimpleWidget({
      sdk: this.sdk as any,
      config: this.config,
    });
  }

  public mount = (): void => {
    // Mount the simplified widget
    this.widget.mount();

    // Listen for position changes from widget
    this.setupPositionChangeListener();

    // Adjust iframe positioning to account for launcher
    this.adjustIframePositioning();
  };

  private setupPositionChangeListener(): void {
    // Listen for position change events from the widget
    window.addEventListener('tronic-launcher-position-changed', () => {
      this.updateIframePositioning();
    });
  }

  private adjustIframePositioning(): void {
    // Adjust the main iframe positioning to avoid overlapping with launcher
    const overlay = (this.sdk as any).overlay as IframeController;
    if (overlay && overlay.container && this.widget.getContainer()) {
      // Get position from widget which handles the priority logic
      const position = this.widget.getCurrentPosition();

      // Add launcher-specific class to ensure iframe respects launcher space
      const launcherClass = `with-launcher-${position}`;
      overlay.container.classList.add(launcherClass);

      // Calculate actual launcher width and apply dynamic positioning
      this.applyDynamicIframePositioning(overlay, position);
    }
  }

  private applyDynamicIframePositioning(overlay: any, position: string): void {
    // Wait for launcher to be fully rendered to get accurate dimensions
    setTimeout(() => {
      const widgetContainer = this.widget.getContainer();
      if (!widgetContainer || !overlay.container) return;

      const launcherRect = widgetContainer.getBoundingClientRect();
      const launcherWidth = launcherRect.width;

      // Get current iframe state from IframeController
      const isMobile = window.innerWidth <= 600;

      // Create configuration for iframe positioning
      const config: IframePositioningConfig = {
        position: position as 'top-right' | 'bottom-right' | 'bottom-left',
        isMobile,
        launcherWidth,
        gap: 20,
        margin: 20,
      };

      // Apply positioning using the centralized css-in-js function
      applyIframePositioning(overlay.container, config);
    }, 100); // Small delay to ensure rendering is complete
  }

  /**
   * Update iframe positioning when state changes (called by IframeController)
   * @public
   */
  public updateIframePositioning(): void {
    const overlay = (this.sdk as any).overlay as IframeController;
    if (overlay && overlay.container && this.widget.getContainer()) {
      // Get position from widget which handles the priority logic
      const position = this.widget.getCurrentPosition();

      // Use the same dynamic positioning logic for consistency
      this.applyDynamicIframePositioning(overlay, position);
    }
  }

  public destroy(): void {
    this.widget.destroy();
  }
}
